import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { DealInvoicesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { DealUpdateModal } from 'modules/finance/components/Deals/UpdateModal'
import { SaleInvoicesListTable } from 'modules/finance/components/Invoices/SaleInvoicesListTable'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const DealInvoicesPage = () => {
  const { dealId } = useParams()
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const { company, access, employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()

  useEffect(() => {
    track(trackingEvents.accounting.deals.viewDealInvoices)
  }, [])

  const {
    data,
    loading: loadingQuery,
    error,
  } = useQuery(DealInvoicesDocument, {
    variables: {
      companyId: company.id,
      dealId: Number(dealId),
      featureIds: [Features.sales_invoices_creation],
    },
  })

  const createInvoicesFeatureAvailable =
    data?.finance?._policies?.financialDocumentsCreateSale?.can ?? false

  const legalEntitiesOptions =
    data?.companies.legalEntitiesConnection.nodes.map((legalEntity) => ({
      label: legalEntity.legalName || '',
      value: legalEntity.id,
    })) || []

  const featureUsage: FeatureAvailabilityType = data?.companies.featureAvailabilitiesConnection
    .nodes[0] ?? {
    available: false,
    id: Features.sales_invoices_creation,
    _internalType: 'feature',
  }

  const availableCurrencies = data
    ? data.apiCore.currenciesConnection.edges.map((edge) => edge.node)
    : []

  const deal = data?.finance?.deal
  const usage = featureUsage.usage || 0
  const usageLimit = featureUsage.usageLimit || -1

  const handleOpenModal = (element: string) => {
    setIsUpdateModalOpen(true)
    track(trackingEvents.accounting.deals.startEdit, {
      source: 'sales_deal_details_page',
      element: element,
    })
  }
  const handleDealUpdated = () => {
    track(trackingEvents.accounting.deals.submitEdit, {
      source: 'sales_deal_details_page',
    })
  }

  return (
    <ResponsivePage.Body
      variant="full"
      error={error}
      loading={loadingQuery}
      onActionClick={(key) => {
        switch (key) {
          case 'edit-deal':
            handleOpenModal('header_edit-deal')
            break
          default:
            break
        }
      }}
    >
      <SaleInvoicesListTable
        companyId={company.id}
        locale={access.locale}
        legalEntitiesOptions={legalEntitiesOptions}
        availableCurrencies={availableCurrencies}
        showDeleteModalForInvoiceId={(invoiceId) => showArchiveSaleInvoiceDialog({ invoiceId })}
        dealId={Number(dealId)}
        detailPath={(invoiceId) =>
          navigation.finance.sales.salesInvoices.detail.fullPath({ invoiceId })
        }
        createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
        usage={usage}
        usageLimit={usageLimit}
        currentEmployeeId={currentEmployee?.id || -1}
      />
      {deal && (
        <DealUpdateModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          deal={deal}
          onSuccess={handleDealUpdated}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default DealInvoicesPage
